import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { cn } from "../../../../../util/cn";
import Meta from "../../../../custom-ui/meta/Meta";
import {
  DataTable,
  IsSortedDataTableIcon,
} from "../../../../custom-ui/new-ui/DataTable";
import BuyCredits from "../Component/CurrentPlan/BuyCredits";
import SemiCircularProgress from "../Component/CurrentPlan/SemiCircularProgress";
import BulletTextWrapper from "../Component/CurrentPlan/BulletTextWrapper";
import { requireAuth, useAuth } from "../../../../../providers/auth";
import {
  getCurrentPlanText,
  useEmployer,
  useEmployerCreditsByEmployerId,
} from "../../../../../data/employer";
import moment from "moment";
import { useAllEmployerJobsWithCreditUsedByEmployerIdFromJobDetailsNewCollection } from "../../../../../data/jobDetailsNewCollection";
import CancelMonthlyPlanModal from "../Component/CurrentPlan/CancelMonthlyPlanModal";
import CancelAnnualPlanModal from "../Component/CurrentPlan/CancelAnnualPlanModal";
import BuyCreditsAnnualPlanModal from "../Component/CurrentPlan/BuyCreditsAnnualPlan";
import { cancelStripeSubscription } from "../../../../../callable-cloud-functions/cloudFunctions";
import SpeakWithAccountExecutiveModal from "../Component/CurrentPlan/SpeakWithAccountExecutiveModal";
import SpeakWithAccountExecutiveThankYouModal from "../Component/CurrentPlan/SpeakWithAccountExecutiveThankYouModal";
import useModal from "../../../../../hooks/useModal";
import ContactSalesSupport from "../Component/CurrentPlan/ContactSalesSupport";
import TermsAndConditionPage from "../../../New_UI/Components/TermsAndCondition";
import ToastAlert from "../../../../custom-ui/toast-alert/ToastAlert";
import { convertTimestamp_subAdminNew } from "../util";
import { stripePlansPrices } from "../../../../../util/stripe/stripeUtils";
import { Timestamp } from "firebase/firestore";
import DialogWrapper from "../../../../custom-ui/dialog/Dialog_NewUI";


const ONE_DAY_SECONDS = 86400;

type CurrentPlanDatatable = {
  id: string;
  jobTitle: string;
  jobStatus: string;
  postedBy: string;
  postingDate: string;
  expiringDate: string;
};

type Props = {};

const CurrentPlan_NewUI = (props: Props) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const employerId = user?.claims?.employerId;
  const { isShowing: showGetInTouchModal, toggle: toggleShowGetInTouchModal } =
    useModal();
  const [showTermsAndCondition, setShowTermsAndCondition] = useState(false);


  const { data: employer } = useEmployer(employerId ?? "");

  useEffect(() => {
    if (employer) {
      if (!(employer?.signedBy)) {
        setShowTermsAndCondition(true)
      }
    }

  }, [employer]);

  const { data: employerCredits, isLoading: isLoadingEmployerCredits } =
    useEmployerCreditsByEmployerId(employerId);

  const { data: jobsWithCreditUsed, isLoading: isLoadingJobsWithCreditUsed } =
    useAllEmployerJobsWithCreditUsedByEmployerIdFromJobDetailsNewCollection(
      employerId,
    );

  const [searchParams] = useSearchParams();

  const isBuyMoreCreditsRedirected = searchParams?.get("buyMoreCredits");

  const [isBuyMoreCreditsModalVisible, setIsBuyMoreCreditsModalVisible] =
    useState(isBuyMoreCreditsRedirected === "true" ? true : false);
  const [
    isBuyMoreAnnualCreditsModalVisible,
    setIsBuyMoreAnnualCreditsModalVisible,
  ] = useState(false);
  const [isCancelMonthlyPlanModalVisible, setIsCancelMonthlyPlanModalVisible] =
    useState(false);
  const [isCancelAnnualPlanModalVisible, setIsCancelAnnualPlanModalVisible] =
    useState(false);
  const [
    isAnnualSpeakWithAccountExecutiveModalVisible,
    setIsAnnualAccountExecutiveLeads,
  ] = useState(false);
  const [
    isAnnualSpeakWithAccountExecutiveThankYouModalVisible,
    setIsAnnualSpeakWithAccountExecutiveThankYouModalVisible,
  ] = useState(false);
  const [
    speakWithAccountExecutiveModalType,
    setSpeakWithAccountExecutiveModalType,
  ] = useState("request");
  const [totalCredits, setTotalCredits] = useState([]);
  const [usedCredits, setUsedCredits] = useState([]);
  const [creditExpiryData, setCreditExpiryData] = useState([]);
  const [isCancelPlanDisabled, setIsCancelPlanDisabled] = useState(false);

  const handleBuyMoreCredits = () => {
    if (["single", "monthly", null].includes(employer?.currentPlan)) {
      setIsBuyMoreAnnualCreditsModalVisible(false);
      setIsBuyMoreCreditsModalVisible(true);
    } else if (employer?.currentPlan === "annual") {
      setIsBuyMoreCreditsModalVisible(false);
      setIsBuyMoreAnnualCreditsModalVisible(true);
    } else if (employer?.currentPlan === "annual12" || employer?.currentPlan === "annual24") {
      setIsBuyMoreAnnualCreditsModalVisible(false);
      setIsBuyMoreCreditsModalVisible(true);

    }
  };

  const closeBuyMoreCreditsModal = () => {
    setIsBuyMoreCreditsModalVisible(false);
  };

  const closeBuyMoreAnnualCreditsModal = () => {
    setIsBuyMoreAnnualCreditsModalVisible(false);
  };

  const onSubmitBuyMoreAnnualCreditsModal = () => {
    setSpeakWithAccountExecutiveModalType("request");
    setIsBuyMoreAnnualCreditsModalVisible(false);
    setIsCancelAnnualPlanModalVisible(false);
    setIsAnnualAccountExecutiveLeads(true);
  };

  const handleCancelPlan = () => {
    if (employer?.currentPlan === "monthly") {
      setIsCancelAnnualPlanModalVisible(false);
      setIsCancelMonthlyPlanModalVisible(true);
    } else if (employer?.currentPlan === "annual") {
      setIsCancelMonthlyPlanModalVisible(false);
      setIsCancelAnnualPlanModalVisible(true);
    } else if (employer?.currentPlan === "annual12" || employer?.currentPlan === "annual24") {
      setIsCancelAnnualPlanModalVisible(false);
      setIsCancelMonthlyPlanModalVisible(true);
    }
  };

  const closeCancelMonthlyPlanModal = () => {
    setIsCancelMonthlyPlanModalVisible(false);
    setIsCancelPlanDisabled(false);
  };

  const closeCancelAnnualPlanModal = () => {
    setIsCancelAnnualPlanModalVisible(false);
    setIsCancelPlanDisabled(false);
  };

  const onSubmitCancelAnnualPlanModal = () => {
    setSpeakWithAccountExecutiveModalType("cancel");
    setIsAnnualAccountExecutiveLeads(true);
    setIsCancelAnnualPlanModalVisible(false);
  };

  const onProceedCancelMonthlyPlanModal = async () => {
    try {
      // get the subscription from employer and supply it to the argument of the cancelStripeSubscription function
      setIsCancelPlanDisabled(true);
      const cancelPlan = await cancelStripeSubscription({
        subscriptionId: employer?.stripeData?.subscriptionId,
        employerId: employer?.id,
      });
      setIsCancelMonthlyPlanModalVisible(false);
    } catch (error) {
      setIsCancelMonthlyPlanModalVisible(false);
    } finally {
        setIsCancelPlanDisabled(false);
    }
  };

  const closeAnnualSpeakWithAccountExecutiveModal = () => {
    setIsAnnualAccountExecutiveLeads(false);
  };

  const onCancelAnnualSpeakWithAccountExecutiveModal = () => {
    setIsAnnualAccountExecutiveLeads(false);
  };

  const onSubmitAnnualSpeakWithAccountExecutiveModal = () => {
    setIsAnnualAccountExecutiveLeads(false);
    setIsCancelAnnualPlanModalVisible(false);
    setIsAnnualSpeakWithAccountExecutiveThankYouModalVisible(true);
  };

  const closeAnnualSpeakWithAccountExecutiveThankYouModal = () => {
    setIsAnnualSpeakWithAccountExecutiveThankYouModalVisible(false);
  };

  const onSubmitAnnualSpeakWithAccountExecutiveThankYouModal = () => {
    setIsAnnualSpeakWithAccountExecutiveThankYouModalVisible(false);
  };

  const handleUpdateCardClick = () => {
    navigate("/billing-information");
  };

  const getAnnualSpeakWithAccountExecutiveModalSource = useMemo(() => {
    if (speakWithAccountExecutiveModalType === "request") {
      return "annualSubscriptionRenewal";
    } else if (speakWithAccountExecutiveModalType === "cancel") {
      return "annualSubscriptionCancel";
    }

    return "annualSubscriptionRenewal";
  }, [speakWithAccountExecutiveModalType]);

  const currentPlanText = useMemo(
    () => getCurrentPlanText(employer?.currentPlan),
    [employer?.currentPlan],
  );

  const remainingCreditsCount = useMemo(
    () => totalCredits?.length - usedCredits?.length,
    [totalCredits, usedCredits],
  );

  const subscriptionExpiry = useMemo(() => {
    if (!employer?.lastMonthlyCreditAdded?.seconds) return null;

    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    const lastCreditAddedSeconds = employer?.lastMonthlyCreditAdded.seconds;

    const newSubscriptionExpiry = lastCreditAddedSeconds + 30 * ONE_DAY_SECONDS;

    // Checking if the new subscription expiry is in the future then only setting the subscription expiry otherwise the subscription expiry will be null as then the current monthly or annual plan would have been expired and there will be no active plan
    if (currentTimeInSeconds < newSubscriptionExpiry) {
      return newSubscriptionExpiry;
    }

    return null;
  }, [employer]);

  useEffect(() => {
    if (isLoadingEmployerCredits) return;

    function isExpired(expireAt: any) {

      const now = Timestamp.now();
      return expireAt.toMillis() <= now.toMillis();
    }

    const totalCreditsData = employerCredits?.filter((employerCredit: any) => {

      if (employer?.currentPlan !== "annual" && employer?.currentPlan !== "single" && employer?.currentPlan != null) {
        return (employerCredit?.createdAt?.toMillis() >= employer?.lastMonthlyCreditAdded?.toMillis()) || (employerCredits.mechanism === "single" && !(isExpired(employerCredit.expireAt)));
      } else {
        return (
          !(isExpired(employerCredit.expireAt))
        );
        return;
      }

    });

    const usedCreditsData = totalCreditsData?.filter((employerCredits: any) => {
      return employerCredits.status === "used";
    });

    setTotalCredits(totalCreditsData);
    setUsedCredits(usedCreditsData);

    const unusedCreditsData = totalCreditsData?.filter(
      (employerCredits: any) => {
        return employerCredits.status === "unused";
      },
    );

    const mappedExpiryWithCreditsCount: { [key: number]: number } = {};

    unusedCreditsData?.forEach((employerCredits: any) => {
      mappedExpiryWithCreditsCount[employerCredits.expireAt.seconds] =
        (mappedExpiryWithCreditsCount[employerCredits.expireAt.seconds] || 0) +
        1;
    });

    const creditExpiryEntries: any = [];

    for (let i in mappedExpiryWithCreditsCount) {
      creditExpiryEntries.push({
        credits: mappedExpiryWithCreditsCount[i],
        timestamp: i,
      });
    }

    creditExpiryEntries.sort((a: any, b: any) => a.timestamp - b.timestamp);
    setCreditExpiryData(creditExpiryEntries);
  }, [isLoadingEmployerCredits]);

  const jobsWithCreditUsedData = useMemo(() => {
    if (isLoadingJobsWithCreditUsed) return;

    // filtering the jobs which are published such that creditId is not there in the database
    const filteredJobsWithCreditUsed = jobsWithCreditUsed?.filter((job: any) => {
      return job?.creditId;
    });

    const mappedJobsWithCreditUsed = filteredJobsWithCreditUsed?.map(
      (job: any) => {
        return {
          id: job.id,
          jobTitle: job.jobName,
          jobStatus: job.jobState,
          postedBy: job?.postedBy?.userName || "",
          postingDate: job?.firstPublishedDate?.seconds
            ? moment(job?.firstPublishedDate?.seconds, "X").format(
              "DD MMM YYYY",
            )
            : "",
          expiringDate: job?.expirationDate?.seconds
            ? moment(job?.expirationDate?.seconds, "X").format("DD MMM YYYY")
            : "",
        };
      },
    );

    return mappedJobsWithCreditUsed;
  }, [isLoadingJobsWithCreditUsed]);

  const formatTimestamp = (timestamp: any, plan: string) => {
    if (!timestamp || !timestamp.seconds) return "";

    // Convert Firebase timestamp to JavaScript Date
    let date = new Date(timestamp.seconds * 1000);

    // Add 30 days if plan is "monthly"
    if (plan === "monthly") {
      date.setDate(date.getDate() + 30);
    }
    if (plan === "annual" || plan === "annual12" || plan === "annual24") {
      date.setDate(date.getDate() + 365);
    }



    // Format the date in "DD MMM YYYY"
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      <Meta title="Current Plan" />
      <div className="flex flex-col">
        <div className="flex justify-between w-full items-center">
          <p className="text-[#15415E] font-[Gilroy-Bold] font-[400] text-2xl leading-[39.62px]">
            {currentPlanText}
          </p>
          <button
            onClick={toggleShowGetInTouchModal}
            className="text-gray-700 px-8 py-1 rounded-full border-instant-teams-blue-Main border-2 font-[400]  leading-[39.62px]"
          >
            Contact Sales Support
          </button>
        </div>

        {!employer?.currentPlan && employer?.currentPlan == null && employer?.annualPlan != null && (
          <div
            className="w-full min-h-[60px] h-auto bg-[#FFD2D2] mt-[30px] mb-[15px] rounded-[10px] p-[10px] text-[#E02F2F] font-[Gilroy-Medium] font-[400] text-[20px] leading-[24.26px] flex flex-row justify-between items-center gap-[10px]"
            style={{ boxShadow: "0px 0px 34px 0px #1E596D0A" }}
          >
            <p>
              Your annual subscription has automatically expired, To continue using our service, a new plan purchase is required.
            </p>

            <button
              className="font-[Gilroy-SemiBold] font-[400] text-[16px] leading-[19.6px] bg-[#15415E] text-[#FFFFFF] w-[250px] min-h-[40px] h-auto rounded-[70px] px-[21px] py-[10px] whitespace-normal"
              onClick={onSubmitBuyMoreAnnualCreditsModal}
            >
              Speak with an Account Executive
            </button>
          </div>
        )}

        {((employer?.stripeData?.subscriptionId &&
          employer?.stripeData?.subscriptionStatus === "past_due") ||
          (!employer?.stripeData?.subscriptionId &&
            employer?.stripeData?.subscriptionStatus === "canceled" &&
            employer?.stripeData?.reason !== "cancellation_requested")) && (
            <div
              className="w-full min-h-[60px] h-auto bg-[#FFD2D2] mt-[30px] mb-[15px] rounded-[10px] p-[20px] text-[#E02F2F] font-[Gilroy-Medium] font-[400] text-[20px] leading-[24.26px] flex flex-row justify-between items-center gap-[10px]"
              style={{ boxShadow: "0px 0px 34px 0px #1E596D0A" }}
            >
              <p>
                Your subscription is currently paused due to a payment issue.
                Please update your card Information.
              </p>

              <button
                className="font-[Gilroy-SemiBold] font-[400] text-[16px] leading-[19.6px] bg-[#15415E] text-[#FFFFFF] w-[150px] min-h-[40px] h-auto rounded-[70px] px-[21px] py-[10px] whitespace-nowrap"
                onClick={handleUpdateCardClick}
              >
                Update Card
              </button>
            </div>
          )}

        <div className="flex flex-col mt-[20px] gap-[20px]">
          <div
            className={cn(
              "flex flex-row  w-full bg-[#FFFFFF] h-auto justify-center items-center min-h-[300px] rounded-[20px] p-[20px] space-x-5",
            )}
            style={{
              boxShadow: "0 0 34px rgba(30, 89, 109, 0.04)",
            }}
          >
            <div className="flex flex-col mt-[62px] w-[20%]  justify-center items-center">
              <div className="h-full w-auto flex justify-center items-center ">
                <SemiCircularProgress
                  usedCredits={usedCredits?.length}
                  remainingCredits={remainingCreditsCount || 0}
                  totalCredits={totalCredits?.length}
                />
              </div>
              <div className="flex flex-row rounded-[20px] mt-16 border-[1px] border-[rgba(138, 160, 174, 0.5)] px-[20px] py-[10px] justify-between w-full font-[Gilroy-Bold] font-[400] text-[18px] leading-[24.76px] text-[#15415E]">
                <p>Total Credits:</p>
                <p>{totalCredits?.length}</p>
              </div>
            </div>

            <div className="flex  flex-col gap-[15px] border-[1px] border-[rgba(138, 160, 174, 0.5)] rounded-[20px] w-auto h-[237px] p-[20px] text-[#15415E]">
              <div className="flex flex-row justify-between font-[Gilroy-Bold] font-[400] text-[18px] leading-[24.76px]">
                {/* <p>Used Credits:</p>
                <p>{usedCredits.length}</p> */}
                <p>Remaining Credits:</p>
                <p>{remainingCreditsCount || 0}</p>
              </div>
              <div className="h-[1px] bg-[#C8E3FB] w-full"></div>

              <div className="flex flex-1 flex-col gap-[15px] text-[#15415E] font-[400] overflow-y-auto pr-[10px] mr-[-10px]">
                {creditExpiryData?.map((creditExpiry: any) => {
                  return (
                    <div
                      key={creditExpiry.timestamp}
                      className="flex flex-row justify-between items-center"
                    >
                      <p className="font-[Gilroy-SemiBold] font-[400] text-[16px] leading-[19.6px]">
                        {creditExpiry.credits}
                      </p>
                      <p className="font-[Gilroy-Medium] font-[400] text-[12px] leading-[14.56px]">
                        Expires On:{" "}
                        {moment(creditExpiry.timestamp, "X").format(
                          "DD MMM YYYY",
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>

              {remainingCreditsCount === 0 && (
                // {employer?.currentPlan && remainingCreditsCount === 0 && (
                <button
                  className="bg-[#15415E] w-full rounded-[70px] text-[#FFFFFF] h-[40px] font-[Gilroy-SemiBold] font-[400] text-[16px] leading-[19.6px] disabled:bg-[#8e9ba3]"
                  onClick={handleBuyMoreCredits}
                // disabled={!employer?.currentPlan}
                >
                  Buy More Credits
                </button>
              )}
            </div>

            <div className="min-w-auto w-auto h-auto min-h-[236px]  rounded-[15px] bg-gradient-to-r from-[#0B619A] to-[#4DAAE8] flex-1 flex flex-col text-[#FFFFFF] p-[20px]">
              <div className=" flex justify-between items-start ">
                {!employer?.currentPlan && (
                  <p className="font-[Gilroy-Bold] font-[400] text-[20px] leading-[24.76px] tracking-wider">
                    <span className="font-[Gilroy-Medium] font-[400] text-[20px] leading-[24.26px] ">
                      You currently have no active plan.{" "}
                      {subscriptionExpiry && remainingCreditsCount !== 0 && (
                        // <>
                        //   Your available credits are valid until{" "}
                        //   {moment(subscriptionExpiry, "X").format(
                        //     "DD MMM YYYY",
                        //   )}
                        //   .
                        // </>

                        <>
                          Your available credits are valid until expiry.
                        </>
                      )}
                    </span>
                  </p>
                )}

                {employer?.currentPlan === "single" && (
                  <p className="font-[Gilroy-Bold] font-[400] text-[20px] leading-[24.76px] tracking-wider">
                    <span className="font-[Gilroy-Medium]  text-cetner  font-[400] text-[20px] leading-[24.26px] ">
                      Fuel Your Talent <br /> Pipeline with Ease
                    </span>
                  </p>
                )}

                {["monthly"].includes(employer?.currentPlan) && (
                  <p className="font-[Gilroy-Bold] font-[400] text-[20px] leading-[24.76px] tracking-wider">
                    <span className="font-[Gilroy-Medium] font-[400]  text-cetner  text-[20px] leading-[24.26px] ">
                      Empower Your Hiring Strategy, Month After Month
                    </span>{" "}
                    <br />
                    <span className="font-[Gilroy-Medium]  font-[400] text-[16px] leading-[24.26px] mt-1 block">
                      Auto Renewal Date:{" "}
                      {formatTimestamp(
                        employer?.lastMonthlyCreditAdded,
                        employer?.currentPlan,
                      )}
                    </span>{" "}
                  </p>
                )}

                {["annual12", "annual24"].includes(employer?.currentPlan) && (
                  <p className=" w-full ">
                    <p className="font-[Gilroy-Bold] py-1 w-full font-[400] text-[20px] leading-[24.76px] tracking-wider">{employer?.currentPlan === "annual12" ? "Annual Flex 12" : "Annual Flex 24"}</p>


                    <span className="font-[Gilroy-Medium]  font-[400] text-[16px] leading-[24.26px] mt-1 block">
                      Auto Renewal Date:{" "}
                      {formatTimestamp(
                        employer?.lastMonthlyCreditAdded,
                        employer?.currentPlan,
                      )}
                    </span>{" "}
                  </p>
                )}

                {employer?.currentPlan === "annual" && (
                  <>
                    <p className="font-[Gilroy-Bold] w-full font-[400] text-[20px] leading-[24.76px] tracking-wider">
                      <div className="flex justify-between mb-2  w-full">
                        <div>
                          <p className="text-lg font-sans mb-1">
                            <span className="font-extrabold">
                              {employer?.annualPlan?.noOfCredits}
                            </span>{" "}
                            Job Posting - Live for
                            <span className="font-extrabold"> 30 </span> days
                          </p>
                          {employer?.annualPlan?.orderNumber && (
                            <p className="text-base  font-sans ">
                              {`Inovoice Number: ${employer?.annualPlan?.orderNumber}`}
                            </p>
                          )}
                        </div>
                      </div>
                      {/* <span className="font-[Gilroy-Medium] font-[400]  text-cetner  text-[20px] leading-[24.26px] ">
                        Your Gateway to Strategic Hiring Success
                      </span> */}
                      <span className="font-[Gilroy-Medium] text-cetner  font-[400] text-[16px] leading-[24.26px] mt-1 block">
                        Contract Expiration Date:{" "}
                        {formatTimestamp(
                          employer?.annualPlan?.startDate,
                          employer?.currentPlan,
                        )}
                      </span>{" "}
                    </p>
                  </>
                )}

                <div className="mr-0  ">
                  {["monthly", "annual", "annual12", "annual24"].includes(employer?.currentPlan) && (
                    <button
                      className="underline w-[100px] font-[Gilroy-Bold] font-[400] text-[16px] leading-[19.81px] tracking-wider"
                      onClick={handleCancelPlan}
                      disabled={isCancelPlanDisabled}
                    >
                      Cancel Plan
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-col flex-1 gap-[10px] font-[Gilroy-Regular] font-[400] text-[12px] leading-[14.4px] tracking-wider mt-[20px]">
                {employer?.currentPlan === "single" && (
                  <>
                    <BulletTextWrapper>
                      Includes one 30-day job post
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Utilize anytime within a year
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Features employer profile and logo
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Funnels candidates directly to your ATS
                    </BulletTextWrapper>
                  </>
                )}

                {employer?.currentPlan === "monthly" && (
                  <>
                    <BulletTextWrapper>
                      Includes three (3) 30-day job post credits each month
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Credits refresh monthly to maintain active participation
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Features employer profile and logo
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Funnels candidates directly to your ATS
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Automatic renewal and billing, cancel anytime
                    </BulletTextWrapper>
                  </>
                )}

                {employer?.currentPlan === "annual12" && (
                  <>
                    <BulletTextWrapper>
                      12 job posts included — use anytime within 12 months
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      8% bundled savings — lower cost per post vs single purchase
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Flexible scheduling — post as hiring needs arise
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Year-round visibility — continuous employer profile in front of engaged talent
                    </BulletTextWrapper>

                  </>
                )}

                {employer?.currentPlan === "annual24" && (
                  <>
                    <BulletTextWrapper>
                      24 job posts included — use anytime within 12 months
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      19% bundled savings — biggest discount per post
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Maximum flexibility — post when hiring demand peaks
                    </BulletTextWrapper>
                    <BulletTextWrapper>
                      Always-on visibility — continuous employer profile in front of an engaged talent community
                    </BulletTextWrapper>
                  </>
                )}


                {employer?.currentPlan === "annual" && (
                  <>
                    <BulletTextWrapper>  Annual, renewable contract with Built-For-You pricing</BulletTextWrapper>
                    <BulletTextWrapper>1 Hr. Consultation: Proven Practices for Military Spouse Hiring</BulletTextWrapper>
                    <BulletTextWrapper>
                      Collaborate with our team to tailor the right option for your needs.
                    </BulletTextWrapper>
                  </>
                )}


              </div>
              {/* {["annual12", "annual24"].includes(employer?.currentPlan) && <p className="font-[Gilroy-Bold] py-1 mt-5  w-full font-[400] text-[20px] leading-[24.76px] tracking-wider">{employer?.currentPlan === "annual12" ? `Total: $ ${stripePlansPrices.annual12}` : `Total: $ ${stripePlansPrices.annual12}`}</p>} */}


              {/* {employer?.currentPlan && (
                <button className="underline font-[Gilroy-Bold] font-[400] text-[16px] leading-[19.81px] mb-[5px] tracking-wider">
                  See All Benefits
                </button>
              )} */}
            </div>
          </div>

          <p className="text-[#15415E] font-[Gilroy-Medium] font-[400] text-[20px] leading-[24.26px]">
            Credits Used
          </p>

          <div>
            <DataTable
              columns={columns}
              data={jobsWithCreditUsedData || []}
              isloading={isLoadingJobsWithCreditUsed}
              isInactiveSelected={false}
            /* Commenting onRowClick Prop as it can be used later on */
            // onRowClick={(row) => {
            //   console.log(row, "========>row");
            // }}
            />
          </div>
        </div>
      </div>
      {isBuyMoreCreditsModalVisible && (
        <DialogWrapper
          title=""
          onClose={closeBuyMoreCreditsModal}
          displayHeaderHorizontalLine={false}
          displayCloseIcon={true}
          closeAtEdge={true}
          size="w-[calc(100%-1.5rem)] max-w-[680px]"
          scrollAtEdge={true}
          panelClassName="rounded-[16px]"
          contentClassName="px-12 sm:px-16"
        >
          <BuyCredits
            currentSubscription={employer?.currentPlan || ""}
            employerId={employerId}
            onDone={closeBuyMoreCreditsModal}
          />
        </DialogWrapper>
      )}

      {showTermsAndCondition === true && (
        <DialogWrapper
          title="Terms and Conditions"
          onClose={() => null}
          size="max-w-[40%]"
        >
          <div className="p-4">
            <TermsAndConditionPage
              employerId={employer?.id || ""}
              closeModal={() => setShowTermsAndCondition(false)}
            />
          </div>
        </DialogWrapper>
      )}

      {showGetInTouchModal && (
        <DialogWrapper
          title=""
          onClose={toggleShowGetInTouchModal}
          displayCloseIcon={true}
          closeAtEdge={true}
          displayHeaderHorizontalLine={false}
          size="w-[calc(100%-1.5rem)] max-w-[680px]"
          panelClassName="!p-0 rounded-[16px]"
        >
          <ContactSalesSupport
            onCancel={toggleShowGetInTouchModal}
            onDone={toggleShowGetInTouchModal}
            source={"contactSalesSupport"}
            type={"request"}
          />
        </DialogWrapper>
      )}
      {isBuyMoreAnnualCreditsModalVisible && (
        <DialogWrapper
          title="&nbsp;"
          onClose={closeBuyMoreAnnualCreditsModal}
          displayCloseIcon={true}
          displayHeaderHorizontalLine={false}
          size="max-w-[630px]"
        >
          <BuyCreditsAnnualPlanModal
            onSubmit={onSubmitBuyMoreAnnualCreditsModal}
          />
        </DialogWrapper>
      )}
      {isCancelMonthlyPlanModalVisible && (
        <DialogWrapper
          title=""
          onClose={closeCancelMonthlyPlanModal}
          displayCloseIcon={false}
          size="max-w-[530px]"
        >
          <CancelMonthlyPlanModal
            onCancel={() => setIsCancelMonthlyPlanModalVisible(false)}
            isCancelPlanDisabled={isCancelPlanDisabled}
            onProceed={onProceedCancelMonthlyPlanModal}
            employer={employer}
          />
        </DialogWrapper>
      )}
      {isCancelAnnualPlanModalVisible && (
        <DialogWrapper
          title="&nbsp;"
          onClose={closeCancelAnnualPlanModal}
          displayCloseIcon={true}
          displayHeaderHorizontalLine={false}
          size="max-w-[630px]"
        >
          <CancelAnnualPlanModal onSubmit={onSubmitCancelAnnualPlanModal} isCancelPlanDisabled={isCancelPlanDisabled} />
        </DialogWrapper>
      )}
      {isAnnualSpeakWithAccountExecutiveModalVisible && (
        <DialogWrapper
          title="Get In Touch"
          subtitle="Our team is here to help you. Send us a message and we'll get back to you as soon as possible."
          onClose={closeAnnualSpeakWithAccountExecutiveModal}
          displayCloseIcon={true}
          displayHeaderHorizontalLine={true}
          size="w-[calc(100%-1.5rem)] max-w-[480px]"
          panelClassName="!p-5"
        >
          <SpeakWithAccountExecutiveModal
            onCancel={onCancelAnnualSpeakWithAccountExecutiveModal}
            onDone={onSubmitAnnualSpeakWithAccountExecutiveModal}
            type={speakWithAccountExecutiveModalType}
            source={getAnnualSpeakWithAccountExecutiveModalSource}
          />
        </DialogWrapper>
      )}
      {isAnnualSpeakWithAccountExecutiveThankYouModalVisible && (
        <DialogWrapper
          title=""
          onClose={closeAnnualSpeakWithAccountExecutiveThankYouModal}
          displayCloseIcon={true}
          displayHeaderHorizontalLine={true}
          size="max-w-[680px]"
        >
          <SpeakWithAccountExecutiveThankYouModal
            onDone={onSubmitAnnualSpeakWithAccountExecutiveThankYouModal}
          />
        </DialogWrapper>
      )}
      <ToastAlert />
    </>
  );
};

// @ts-ignore
const columns: ColumnDef<CurrentPlanDatatable>[] = [
  {
    accessorKey: "jobTitle",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex flex-row gap-[15px]"
        >
          Job Title
          <IsSortedDataTableIcon getIsSorted={column.getIsSorted()} />
        </button>
      );
    },
    cell: ({ row }) => {
      return <div>{row.getValue("jobTitle")}</div>;
    },
  },
  {
    accessorKey: "jobStatus",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex flex-row gap-[15px]"
        >
          Job Status
          <IsSortedDataTableIcon getIsSorted={column.getIsSorted()} />
        </button>
      );
    },
    cell: ({ row }) => {
      const jobStatusValue: string = row.getValue("jobStatus");

      let jobStatusValueToShow = jobStatusValue;

      switch (jobStatusValue) {
        case "draft":
          jobStatusValueToShow = "Draft";
          break;
        case "on_hold":
          jobStatusValueToShow = "On Hold";
          break;
        case "live":
          jobStatusValueToShow = "Live";
          break;
        case "closed_job_expired":
          jobStatusValueToShow = "Expired";
          break;
        case "closed_filled":
          jobStatusValueToShow = "Closed";
          break;
        case "closed_cancelled":
          jobStatusValueToShow = "Closed";
          break;
        default:
          break;
      }

      return (
        <div
          className={cn(
            "rounded-[50px] w-[94px] h-[36px] p-[10px] font-[Gilroy-Medium] font-[400] text-[16px] leading-[19.41px] flex justify-center items-center",
            {
              "bg-[#CFFFD8] text-[#3DAB51]": jobStatusValue === "live",
              "bg-orange-100 text-[#F7A400]": jobStatusValue === "draft",
              "bg-[#DBDBFF] text-[#6B6BEA]": jobStatusValue === "on_hold",
              "bg-[#FFD2D2] text-[#E02F2F]": [
                "expired",
                "closed_job_expired",
                "closed_cancelled",
                "closed_filled",
              ].includes(jobStatusValue),
            },
          )}
        >
          <p className="first-letter:uppercase">{jobStatusValueToShow}</p>
        </div>
      );
    },
  },
  {
    accessorKey: "postedBy",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex flex-row gap-[15px]"
        >
          Posted By
          <IsSortedDataTableIcon getIsSorted={column.getIsSorted()} />
        </button>
      );
    },
    cell: ({ row }) => {
      return <div>{row.getValue("postedBy")}</div>;
    },
  },
  {
    accessorKey: "postingDate",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex flex-row gap-[15px]"
        >
          Posting Date
          <IsSortedDataTableIcon getIsSorted={column.getIsSorted()} />
        </button>
      );
    },
    cell: ({ row }) => {
      return <div>{row.getValue("postingDate")}</div>;
    },
  },
  {
    accessorKey: "expiringDate",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex flex-row gap-[15px]"
        >
          Expiring Date
          <IsSortedDataTableIcon getIsSorted={column.getIsSorted()} />
        </button>
      );
    },
    cell: ({ row }) => {
      return <div>{row.getValue("expiringDate")}</div>;
    },
  },
];

export default requireAuth(CurrentPlan_NewUI);
